/**
 * Studio Layouts
 *
 * The shell arrangement an owner picks in Appearance settings. Studio-wide:
 * every member of the studio sees the owner's choice.
 *
 * Keep in sync with VALID_LAYOUTS in
 * truman/src/lambda/api/studio-trpc/routers/studio.ts and the `layout` column
 * on StudioPreference.
 */

export type LayoutId = "reader" | "rail" | "spaces" | "console";

/**
 * Where a studio lands when its stored value is unknown, null, or left over
 * from a rolled-back option set.
 *
 * Same value as the column default on `StudioPreference.layout`, for a
 * different reason: that one decides what a NEW studio opens on, this one is
 * the read-side safety net, so a studio with an unreadable or retired value
 * keeps the shell it already had rather than being silently re-arranged.
 */
export const FALLBACK_LAYOUT: LayoutId = "rail";

export interface LayoutOption {
  id: LayoutId;
  name: string;
  description: string;
}

// `satisfies` rather than `: LayoutOption[]` — the annotation would widen each
// `id` back to LayoutId, which silently defeats the exhaustiveness guard in
// lib/__tests__/layout-switch.test.ts. This keeps the literal types while
// still checking the shape.
export const LAYOUT_OPTIONS = [
  {
    id: "reader",
    // PROVISIONAL NAME — Cameron asked for naming ideas and has not confirmed.
    // Changing the id later is a one-line edit here, in VALID_LAYOUTS and in
    // the LAYOUTS map, plus a data migration for any studio already holding
    // it. Free while the feature is gated; not free after.
    name: "Reader",
    description:
      "Navigation across the top, one centred reading column beneath. Like your public pages, signed in.",
  },
  {
    id: "rail",
    // Deliberately not "Classic": in studio-web "classic" names the RETIRED
    // pre-rail design (classic-feed-post.tsx, <FeedPost forceClassic />),
    // deprecated by TRA-237. Not "Current" either — it is the default, but a
    // label that describes the current pick ages badly on a picker.
    name: "Standard",
    description:
      "An icon rail, a sidebar of links, and one content column. The long-standing design.",
  },
  {
    id: "spaces",
    // The prototype's `nav: sidebar` arrangement. Its LAYOUTS metadata calls
    // this "named rooms on a top bar" — that describes Reader; the prototype
    // code renders a sidebar. The description below matches what it draws.
    name: "Spaces",
    description:
      "A single sidebar of links and one centred reading column. No icon rail.",
  },
  {
    id: "console",
    name: "Console",
    description:
      "Everything in the left sidebar, with what you open filling the right.",
  },
] satisfies readonly LayoutOption[];

/**
 * Anything unknown, null, or left over from a rolled-back value set resolves
 * to FALLBACK_LAYOUT. This is what keeps a studio renderable if the option
 * list ever shrinks.
 */
export const toLayoutId = (value: unknown): LayoutId =>
  LAYOUT_OPTIONS.some((option) => option.id === value)
    ? (value as LayoutId)
    : FALLBACK_LAYOUT;
